import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface StepIndicatorStep {
  label: string;
  description?: string;
}

interface StepIndicatorProps {
  steps: StepIndicatorStep[];
  currentStep: number;
  className?: string;
}

/**
 * Accessible step indicator component.
 *
 * Uses semantic list markup with aria attributes for screen readers.
 * Active step is marked with aria-current="step".
 */
export function StepIndicator({
  steps,
  currentStep,
  className,
}: StepIndicatorProps) {
  return (
    <nav
      aria-label={`Step ${currentStep + 1} of ${steps.length}: ${steps[currentStep]?.label ?? ""}`}
      className={cn("mb-8", className)}
    >
      <ol role="list" className="flex items-center justify-center gap-2">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;

          return (
            <li
              key={step.label}
              role="listitem"
              aria-current={isCurrent ? "step" : undefined}
              className="flex items-center"
            >
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    "flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium transition-colors",
                    isCompleted || isCurrent
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {isCompleted ? <Check className="h-4 w-4" /> : index + 1}
                </div>
                <span
                  className={cn(
                    "text-xs mt-1.5 text-center whitespace-nowrap",
                    index <= currentStep
                      ? "text-foreground"
                      : "text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
                {step.description && (
                  <span className="text-xs text-muted-foreground text-center hidden sm:block">
                    {step.description}
                  </span>
                )}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "h-0.5 w-12 mx-2 transition-colors self-start mt-4",
                    index < currentStep ? "bg-primary" : "bg-muted"
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
